import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';

// Pages
import HomePage from './pages/Home/HomePage';
import SearchPage from './pages/Search/SearchPage';
import ServerProfilePage from './pages/ServerProfile/ServerProfilePage';
import PricingPage from './pages/Pricing/PricingPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import ReviewPage from './pages/Review/ReviewPage';
import QuoteViewPage from './pages/Quote/QuoteViewPage';
import PublicInvoicePage from './pages/Invoice/PublicInvoicePage';
import PublicStatementPage from './pages/Statement/PublicStatementPage';
import AdminPanel from './pages/Admin/AdminPanel';
import ResourcesPage from './pages/Resources/ResourcesPage';
import TermsPage from './pages/Legal/TermsPage';
import PrivacyPage from './pages/Legal/PrivacyPage';
import ContactPage from './pages/Contact/ContactPage';
import SlugRedirect from './pages/SlugRedirect/SlugRedirect';
import TrackRoute from './pages/TrackRoute';
import ScrollToTop from './components/ScrollToTop';
import { CommandPalette } from './components/CommandPalette/CommandPalette';
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from './pages/Auth/AuthPages';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-slate-300 border-t-slate-600 rounded-full"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin h-8 w-8 border-4 border-slate-600 border-t-teal-400 rounded-full"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role !== 'admin' && user?.role !== 'moderator') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <ScrollToTop />
            <CommandPalette />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/servers/:id" element={<ServerProfilePage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/review/:token" element={<ReviewPage />} />
              <Route path="/quote/:clientToken" element={<QuoteViewPage />} />
              <Route path="/invoice/:invoiceId" element={<PublicInvoicePage />} />
              <Route path="/statement/:statementId" element={<PublicStatementPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/s/:slug" element={<SlugRedirect />} />
              <Route path="/track/:shareToken" element={<TrackRoute />} />
              
              {/* Protected Routes */}
              <Route 
                path="/dashboard/*" 
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Admin Routes */}
              <Route 
              path="/admin/*" 
              element={
                <AdminRoute>
                  <AdminPanel />
                </AdminRoute>
              } 
            />
            
            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
